import type {Suggestion, Suggestions} from '@github-ui/inline-autocomplete/types'
import {useQuery} from '@github-ui/react-query'
import {useMemo} from 'react'

import {useRepositoryFilesQuery} from '../../hooks/use-repository-files-query'
import {useChatManager} from '../../utils/CopilotChatManagerContext'
import {ReferenceMention} from '../../utils/reference-mention'
import {FileSuggestion} from './FileSuggestion'

export interface FilesQuery {
  category: 'files'
  repository: string
  filter: string
}

export function useFilesSuggestions(query: FilesQuery | null) {
  const manager = useChatManager()

  const repoQuery = useQuery({
    enabled: query !== null,
    queryKey: ['copilot-repository', query?.repository],
    queryFn: async () => {
      if (!query?.repository) return null

      const response = await manager.service.fetchRepo(query?.repository)

      if (!response.ok) throw new Error('Failed to fetch repository')

      return response.payload
    },
  })

  const filesQuery = useRepositoryFilesQuery(repoQuery.data ?? null, query?.filter ?? '')

  return useMemo<Suggestions | null>(() => {
    if (!query) return null

    if (
      repoQuery.isLoading ||
      filesQuery.query.isLoading ||
      filesQuery.filteredPathsQuery.isLoading ||
      filesQuery.filteredDirectoriesQuery.isLoading
    )
      return 'loading'

    const result = [
      ...(filesQuery.filteredPathsQuery.data?.slice(0, 5).map<Suggestion>(file => ({
        value: ReferenceMention.stringify({
          repo: query.repository,
          type: 'file',
          id: file,
        }),
        key: file,
        render: props => <FileSuggestion {...props} path={file} type="file" />,
      })) ?? []),
      ...(filesQuery.filteredDirectoriesQuery.data?.slice(0, 5).map<Suggestion>(directory => ({
        value: ReferenceMention.stringify({
          repo: query.repository,
          type: 'file',
          id: directory,
        }),
        key: directory,
        render: props => <FileSuggestion {...props} path={directory} type="folder" />,
      })) ?? []),
    ]

    return result.length > 0 ? result : null
  }, [
    filesQuery.filteredDirectoriesQuery.data,
    filesQuery.filteredDirectoriesQuery.isLoading,
    filesQuery.filteredPathsQuery.data,
    filesQuery.filteredPathsQuery.isLoading,
    filesQuery.query.isLoading,
    query,
    repoQuery.isLoading,
  ])
}
